import BookIndex from "components/shared/book/list";
import SharedReactTable from "components/shared/reactTable";
import { dateTimeInUtcOffset, dateTimeToView, formatUtcOffset } from "helpers/date";
import { tripSheetStatuses } from "helpers/enums/tripSheetStatuses";
import { paths } from "helpers/paths";
import {
    BooleanColumnFilter,
    DateColumnFilter,
    EnumColumnFilter,
    rowIndexColumn,
    SelectColumnFilter,
} from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { enumLabel } from "helpers/system";
import React from "react";
import { Link } from "react-router-dom";
import { namesList as driverNamesList } from "sources/books/drivers";
import { numbersList as trailerNumbersList } from "sources/books/trailers";
import { numbersList as truckNumbersList } from "sources/books/trucks";

const getDriverNames = (original) => {
    const rawNames =
        Array.isArray(original.driverNames) && original.driverNames.length > 0
            ? original.driverNames
            : [original.driversSummary?.split(","), original.driverFullName, original.secondaryDriverFullName].flat();

    return Array.from(
        new Set(
            rawNames
                .filter(Boolean)
                .map((name) => String(name).trim())
                .filter(Boolean),
        ),
    ).join(", ");
};

const getVersionLabel = ({ documentVersion, correctedTripSheetId }) =>
    `${correctedTripSheetId ? "Корректировочная версия" : "Версия"} ${documentVersion || 1}`;

class TripSheetsList extends BookIndex {
    dateTimeToOrganizationView = (value, mask) => {
        if (!value) {
            return "";
        }
        const organizationUtcOffset = formatUtcOffset(this.props.organizationTimezone);
        const date = organizationUtcOffset ? dateTimeInUtcOffset(value, organizationUtcOffset) : value;
        return dateTimeToView(date, mask);
    };

    get tableColumns() {
        return [
            rowIndexColumn,
            {
                Header: "ID",
                accessor: "id",
                width: 80,
                filterable: true,
                filterType: "int",
                Cell: ({ value }) => <Link to={paths.tripSheetsShow(value)}>{value}</Link>,
            },
            {
                Header: "Номер",
                accessor: "number",
                filterable: true,
                Cell: ({ value, row: { original } }) => (
                    <Link to={paths.tripSheetsShow(original.id)}>
                        <span className="text-nowrap mr-1">{value}</span>
                        <small className="text-muted text-nowrap">({getVersionLabel(original)})</small>
                    </Link>
                ),
                CSVExport: ({ value, original }) => `${value} (${getVersionLabel(original)})`,
            },
            {
                Header: "Статус",
                accessor: "status",
                filterable: true,
                filterType: "enum",
                Filter: (props) => <EnumColumnFilter {...props} filterEnum={tripSheetStatuses} />,
                Cell: ({ value }) => enumLabel(tripSheetStatuses, value),
            },
            {
                Header: "Дата создания",
                accessor: "createdAt",
                sortable: true,
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY HH:mm"),
                CSVExport: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY HH:mm"),
            },
            {
                Header: "Дата выезда",
                accessor: "validFrom",
                sortable: true,
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY"),
                CSVExport: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY"),
            },
            {
                Header: "Дата возврата",
                accessor: "validTo",
                sortable: true,
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY"),
                CSVExport: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY"),
            },
            {
                Header: "Фактическая дата выезда",
                accessor: "actualDepartureAt",
                sortable: true,
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY HH:mm") || "—",
                CSVExport: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY HH:mm") || "",
            },
            {
                Header: "Фактическая дата возврата",
                accessor: "actualReturnAt",
                sortable: true,
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY HH:mm") || "—",
                CSVExport: ({ value }) => this.dateTimeToOrganizationView(value, "DD.MM.YYYY HH:mm") || "",
            },
            {
                Header: "Водитель 1",
                accessor: "driverFullName",
                filterable: true,
                filterType: "select",
                id: "driverId",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: driverNamesList,
                            inputProps: { name: "driverId" },
                            propName: "fullName",
                        }}
                    />
                ),
            },
            {
                Header: "Все водители",
                id: "drivers",
                sortable: false,
                accessor: getDriverNames,
                Cell: ({ value }) => value || <span className="text-muted">Не указаны</span>,
                CSVExport: ({ value }) => value || "",
            },
            {
                Header: "Кол-во заявок",
                accessor: "assignmentCount",
                width: 75,
                sortable: false,
                Cell: ({ row: { original } }) => original.assignmentCount ?? 0,
                CSVExport: ({ value }) => value ?? 0,
            },
            {
                Header: "Пробег, км",
                accessor: "actualMileage",
                width: 100,
                sortable: false,
                Cell: ({ row: { original } }) => original.actualMileage ?? "—",
                CSVExport: ({ value }) => value ?? "—",
            },
            {
                Header: "Незаполненные факты",
                accessor: "hasIncompleteFacts",
                width: 105,
                sortable: false,
                filterable: true,
                filterType: "boolean",
                Filter: (props) => (
                    <BooleanColumnFilter
                        {...props}
                        options={[
                            { label: "Показать все", value: "all" },
                            { label: "Есть незаполненные", value: "true" },
                            { label: "Все заполнены", value: "false" },
                        ]}
                    />
                ),
                Cell: ({ value }) => (
                    <div className="overflow-hidden">
                        {value ? (
                            <span className="badge badge-danger">Есть незаполненные</span>
                        ) : (
                            <span className="badge badge-success">Все заполнены</span>
                        )}
                    </div>
                ),
                CSVExport: ({ value }) => (value ? "Есть незаполненные" : "Все заполнены"),
            },
            {
                Header: "Госномер ТС",
                accessor: "carNumber",
                filterable: true,
                filterType: "select",
                id: "carId",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: truckNumbersList,
                            inputProps: { name: "carId" },
                            propName: "number",
                        }}
                    />
                ),
            },
            {
                Header: "Госномер прицепа",
                accessor: "trailerNumber",
                filterable: true,
                filterType: "select",
                id: "trailerId",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: trailerNumbersList,
                            inputProps: { name: "trailerId" },
                            propName: "number",
                        }}
                    />
                ),
            },
        ];
    }

    renderCreateButton = () => {
        const { history } = this.props;
        return (
            <button type="button" className="btn btn-brand btn-sm" onClick={() => history.push(paths.tripSheetsNew)}>
                Создать путевой лист
            </button>
        );
    };

    renderListComponent = () => {
        return <SharedReactTable {...this.props} columns={this.state.tableColumns} />;
    };
}

export default tableContainer(TripSheetsList, "trip-sheets");
